import { Autocomplete, TextField } from "@mui/material";
import React, { SyntheticEvent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

interface ThemeAutocompleteProps {
  /**
   * The name of the selected theme.  Use when the component is controlled.
   */
  theme?: string;
  /**
   * The name of the theme selected by default.  Use when the component is not controlled.
   */
  themeDefault?: string;
  /**
   * Callback fired when the theme is changed.
   */
  onChange(value: string): void;
}

interface ThemeOption {
  name: string;
  value: string;
}

export default function ThemeAutocomplete(props: ThemeAutocompleteProps): React.JSX.Element {
  const { i18n } = useTranslation();

  const [themeState, setThemeState] = useState<string>(props.themeDefault ?? "PureLightTheme");

  const themeOptions = useMemo<Array<ThemeOption>>(() => {
    return [{
      name: i18n.t("theme-autocomplete.options.dark"),
      value: "DarkSpacesTheme"
    }, {
      name: i18n.t("theme-autocomplete.options.light"),
      value: "PureLightTheme"
    }]
  }, [i18n]);

  // Abstract controlled vs uncontrolled usages of this component.
  const theme = props.theme ?? themeState;

  const selectedTheme = useMemo((): ThemeOption => {
    const found = themeOptions.find((option) => {
      return option.value === theme;
    });
    return found ?? themeOptions[0];
  }, [theme])

  function handleThemeChange(event: SyntheticEvent, option: ThemeOption): void {
    if (!props.theme) {
      // This is an uncontrolled component that needs to handle its own state.
      setThemeState(option.value);
    }
    props.onChange(option.value);
  }

  return (
    <Autocomplete 
      fullWidth={true}
      disableClearable
      getOptionLabel={(option) => option.name}
      options={themeOptions}
      value={selectedTheme}
      isOptionEqualToValue={(a: ThemeOption, b: ThemeOption) => a.value === b.value}
      onChange={handleThemeChange}
      renderInput={(params) => <TextField autoComplete="off" {...params} label="" />} />
  )
}